import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiEye } from 'react-icons/fi';
import { toast } from '../utils/customToast';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../utils/api';
import { useApiErrorHandler } from '../hooks/useApiErrorHandler';
import RichTextEditor from '../components/messages/RichTextEditor';
import SuggestionDropdown from '../components/shared/SuggestionDropdown';
import DateDropdown from '../components/shared/DateDropdown';
import { filterDateCommands } from '../utils/dateCommandUtils';
import PreviewTemplateModal from '../components/modals/PreviewTemplateModal';
import DeleteTemplateModal from '../components/modals/DeleteTemplateModal';
import useMessageSettings from '../hooks/useMessageSettings';
import { usePlanPermissions, getCurrentPlan, UpgradeMessage } from '../utils/planPermissions';
import usePageTitle from '../hooks/usePageTitle';

const MESSAGE_TYPE_COLORS = {
    'Rappel mensuel': '#3b82f6',
    'Rappel hebdomadaire': '#8b5cf6',
    'Rappel quotidien': '#06b6d4',
    'Notification': '#f59e0b',
    'Information': '#10b981',
    'Alerte': '#ef4444',
    'Mise à jour': '#6366f1'
};

const MessageTemplates = () => {
    usePageTitle('Modèles de messages');
    const { getAuthHeaders, activeTenantId, user } = useAuth();
    const { handleApiError } = useApiErrorHandler();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    
    // Form fields
    const [name, setName] = useState('');
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [type, setType] = useState('');
    const [role, setRole] = useState('');
    const [priority, setPriority] = useState('moyenne');
    const [saving, setSaving] = useState(false);
    
    // Modal states
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    
    const editorRef = useRef(null);
    const { messageTypes, messageRoles } = useMessageSettings();

    // Permissions basées sur le plan
    const currentPlan = getCurrentPlan(user, activeTenantId);
    const { canMentionUsers, canMentionTodos, canMentionMessages, canUseDateCommands } = usePlanPermissions(currentPlan);
    
    // States for mentions
    const [allMessages, setAllMessages] = useState([]);
    const [allTodos, setAllTodos] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [suggestOpen, setSuggestOpen] = useState(false);
    const [suggestItems, setSuggestItems] = useState([]);
    const [suggestAnchor, setSuggestAnchor] = useState(null);
    const [suggestActive, setSuggestActive] = useState(0);

    // States for date dynamic commands
    const [dateSuggestOpen, setDateSuggestOpen] = useState(false);
    const [dateSuggestItems, setDateSuggestItems] = useState([]);
    const [dateSuggestAnchor, setDateSuggestAnchor] = useState(null);
    const [dateSuggestActive, setDateSuggestActive] = useState(0);
    
    const apiBase = process.env.REACT_APP_API_URL || '/api';

    // Normalize types to array of objects { name, color }
    const normalizedTypes = React.useMemo(() => {
        if (messageTypes && messageTypes.length) {
            return messageTypes.map(t =>
                typeof t === 'string'
                    ? { name: t, color: MESSAGE_TYPE_COLORS[t] || '#000000' }
                    : { name: t.name, color: t.color || MESSAGE_TYPE_COLORS[t.name] || '#000000' }
            );
        }
        return [];
    }, [messageTypes]);

    const roles = (messageRoles && messageRoles.length) ? messageRoles : [];

    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiFetch(
                `${apiBase}/message-templates`,
                { headers: getAuthHeaders() },
                handleApiError
            );
            setTemplates(data);
        } catch (error) {
            console.error('Error fetching templates:', error);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders, handleApiError, apiBase]);

    useEffect(() => {
        if (activeTenantId) {
            fetchTemplates();
            
            // Fetch data for mentions if plan allows
            (async () => {
                try {
                    const promises = [
                        canMentionMessages ? fetch(`${apiBase}/messages`, { headers: getAuthHeaders() }) : null,
                        canMentionTodos ? fetch(`${apiBase}/todos`, { headers: getAuthHeaders() }) : null,
                        canMentionUsers ? fetch(`${apiBase}/tenants/${activeTenantId}/users`, { headers: getAuthHeaders() }) : null
                    ];

                    const [mRes, tRes, uRes] = await Promise.all(promises);

                    if (mRes && mRes.ok) setAllMessages(await mRes.json());
                    if (tRes && tRes.ok) setAllTodos(await tRes.json());
                    if (uRes && uRes.ok) setAllUsers(await uRes.json());
                } catch (e) {
                    console.error('Error fetching mention data:', e);
                }
            })();
        }
    }, [activeTenantId, canMentionMessages, canMentionTodos, canMentionUsers, fetchTemplates, getAuthHeaders, apiBase]);

    const resetForm = () => {
        setName('');
        setTitle('');
        setContent('');
        setType(normalizedTypes[0]?.name || '');
        setRole(roles[0] || '');
        setPriority('moyenne');
        setEditingTemplate(null);
        setIsCreating(false);
    };

    const handleCreate = () => {
        setIsCreating(true);
        setName('');
        setTitle('');
        setContent('');
        setType(normalizedTypes[0]?.name || '');
        setRole(roles[0] || '');
        setPriority('moyenne');
    };

    const handleEdit = (template) => {
        setEditingTemplate(template);
        setIsCreating(false);
        setName(template.name);
        setTitle(template.title);
        setContent(template.content);
        setType(template.type);
        setRole(template.role);
        setPriority(template.priority || 'moyenne');
    };

    const handleSave = async () => {
        if (!name.trim() || !title.trim() || !content.trim()) {
            toast.error('Tous les champs sont requis');
            return;
        }

        if (!type || !role) {
            toast.error('Le type et le rôle sont requis');
            return;
        }

        setSaving(true);
        try {
            if (editingTemplate) {
                // Update existing template
                await apiFetch(
                    `${apiBase}/message-templates/${editingTemplate._id}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            ...getAuthHeaders()
                        },
                        body: JSON.stringify({ name, title, content, type, role, priority })
                    },
                    handleApiError
                );
                toast.success('Modèle mis à jour avec succès');
            } else {
                // Create new template
                await apiFetch(
                    `${apiBase}/message-templates`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...getAuthHeaders()
                        },
                        body: JSON.stringify({ name, title, content, type, role, priority })
                    },
                    handleApiError
                );
                toast.success('Modèle créé avec succès');
            }
            
            await fetchTemplates();
            resetForm();
        } catch (error) {
            console.error('Error saving template:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleMentionTrigger = (data) => {
        if (!data.isActive) {
            setSuggestOpen(false);
            return;
        }

        const query = (data.query || '').toLowerCase();

        const msgs = canMentionMessages ? (allMessages || [])
            .filter(m => {
                return m.title?.toLowerCase().includes(query) ||
                    m.content?.toLowerCase().includes(query);
            })
            .slice(0, 20)
            .map(m => ({ ...m, kind: 'msg' })) : [];

        const todos = canMentionTodos ? (allTodos || [])
            .filter(t => {
                return t.text?.toLowerCase().includes(query);
            })
            .slice(0, 20)
            .map(t => ({ ...t, kind: 'todo' })) : [];

        const users = canMentionUsers ? (allUsers || [])
            .filter(u => {
                return u.name?.toLowerCase().includes(query) ||
                    u.email?.toLowerCase().includes(query);
            })
            .slice(0, 20)
            .map(u => ({ ...u, kind: 'user' })) : [];

        const items = [...msgs, ...todos, ...users];
        setSuggestItems(items);
        setSuggestActive(0);
        setSuggestOpen(items.length > 0);
        setSuggestAnchor(data.rect);
    };

    const handleMentionClick = () => {
        if (editorRef.current) {
            const editor = editorRef.current;
            
            // Insert @ character
            if (editor.insertContent) {
                editor.insertContent('@');
            }
            
            // Trigger mention dropdown manually
            handleMentionTrigger({ isActive: true, query: '', rect: null });
        }
    };

    const applySuggestion = (item) => {
        let mention = '';
        if (item.kind === 'msg') {
            mention = `@msg:${item._id}`;
        } else if (item.kind === 'todo') {
            mention = `@todo:${item._id}`;
        } else if (item.kind === 'user') {
            mention = `@user:${item._id}[${item.name}]`;
        }

        if (editorRef.current?.insertMention) {
            editorRef.current.insertMention(mention);
        }

        setSuggestOpen(false);

        setTimeout(() => {
            editorRef.current?.focus();
        }, 0);
    };

    const handleDateTrigger = (data) => {
        if (!data.isActive) {
            setDateSuggestOpen(false);
            return;
        }
        const items = filterDateCommands(data.query || '');
        setDateSuggestItems(items);
        setDateSuggestActive(0);
        setDateSuggestOpen(items.length > 0);
        setDateSuggestAnchor(data.rect);
    };

    const applyDateSuggestion = (item) => {
        if (editorRef.current?.insertDateCommand) {
            // Insère une balise persistante {{date:Commande}} résolue dynamiquement à l'affichage
            editorRef.current.insertDateCommand(`{{date:${item.command}}}`);
        }
        setDateSuggestOpen(false);
        setTimeout(() => editorRef.current?.focus(), 0);
    };

    // Navigation clavier
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!suggestOpen) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    e.stopPropagation();
                    setSuggestActive(prev => Math.min(prev + 1, suggestItems.length - 1));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    e.stopPropagation();
                    setSuggestActive(prev => Math.max(prev - 1, 0));
                    break;
                case 'Enter':
                    if (suggestItems[suggestActive]) {
                        e.preventDefault();
                        e.stopPropagation();
                        applySuggestion(suggestItems[suggestActive]);
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    e.stopPropagation();
                    setSuggestOpen(false);
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [suggestOpen, suggestItems, suggestActive]);

    const handlePreview = (template) => {
        setSelectedTemplate(template);
        setShowPreviewModal(true);
    };

    const handleDeleteClick = (template) => {
        setSelectedTemplate(template);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedTemplate) return;

        try {
            await apiFetch(
                `${apiBase}/message-templates/${selectedTemplate._id}`,
                {
                    method: 'DELETE',
                    headers: getAuthHeaders()
                },
                handleApiError
            );
            toast.success('Modèle supprimé avec succès');
            setShowDeleteModal(false);
            setSelectedTemplate(null);
            await fetchTemplates();
        } catch (error) {
            console.error('Error deleting template:', error);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex md:flex-row flex-col justify-between items-start gap-4">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Modèles de messages
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Créez et gérez des modèles pour accélérer la création de messages
                    </p>
                </div>
                {!isCreating && !editingTemplate && (
                    <button
                        onClick={handleCreate}
                        className="flex items-center gap-2 bg-primary hover:bg-primary-hover dark:text-black text-white px-4 py-2 rounded transition"
                    >
                        <FiPlus /> Nouveau modèle
                    </button>
                )}
            </div>

            {/* Creation/Edit Form */}
            {(isCreating || editingTemplate) && (
                <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-6 shadow-md">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        {editingTemplate ? 'Modifier le modèle' : 'Créer un modèle'}
                    </h3>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Nom du modèle *
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Ex: Newsletter, Rappel de réunion..."
                                className="w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:border-primary dark:focus:border-primary focus:ring focus:ring-primary px-3 py-2"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Titre *
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Titre du message"
                                className="w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:border-primary dark:focus:border-primary focus:ring focus:ring-primary px-3 py-2"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Contenu *
                            </label>
                            <RichTextEditor
                                ref={editorRef}
                                content={content}
                                onChange={setContent}
                                placeholder={canMentionUsers || canMentionTodos || canMentionMessages
                                    ? (canUseDateCommands ? "Contenu du message... @ pour mentionner, // pour une date" : "Contenu du message... Utilisez @ pour mentionner")
                                    : (canUseDateCommands ? "Contenu du message... // pour insérer une date dynamique" : "Contenu du message...")}
                                minHeight="200px"
                                onMentionTrigger={handleMentionTrigger}
                                onMentionClick={handleMentionClick}
                                hasMentionSupport={canMentionUsers || canMentionTodos || canMentionMessages}
                                onDateTrigger={canUseDateCommands ? handleDateTrigger : undefined}
                            />
                            {!canMentionMessages && !canMentionTodos && !canMentionUsers ? (
                                <UpgradeMessage 
                                    feature="todoToMessage" 
                                    currentPlan={currentPlan}
                                    className="mt-2"
                                />
                            ) : (
                                <>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        💡 @ pour mentionner {[
                                            canMentionMessages && 'des messages',
                                            canMentionTodos && 'des todos',
                                            canMentionUsers && 'des utilisateurs'
                                        ].filter(Boolean).join(' et ')}{canUseDateCommands ? ' · // pour une date dynamique (ex : //Mois-1, //Année)' : ''}
                                    </p>
                                    {canMentionMessages && canMentionTodos && !canMentionUsers && (
                                        <UpgradeMessage 
                                            feature="userMentions" 
                                            currentPlan={currentPlan}
                                            className="mt-2"
                                        />
                                    )}
                                </>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Type *
                                </label>
                                <select
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                    className="w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:border-primary dark:focus:border-primary focus:ring focus:ring-primary px-3 py-2"
                                >
                                    {normalizedTypes.map(t => (
                                        <option key={t.name} value={t.name}>{t.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Rôle *
                                </label>
                                <select
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                    className="w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:border-primary dark:focus:border-primary focus:ring focus:ring-primary px-3 py-2"
                                >
                                    {roles.map(r => (
                                        <option key={r} value={r}>{r}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Priorité *
                                </label>
                                <select
                                    value={priority}
                                    onChange={(e) => setPriority(e.target.value)}
                                    className="w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:border-primary dark:focus:border-primary focus:ring focus:ring-primary px-3 py-2"
                                >
                                    <option value="critique">Critique</option>
                                    <option value="haute">Haute</option>
                                    <option value="moyenne">Moyenne</option>
                                    <option value="basse">Basse</option>
                                    <option value="minimale">Minimale</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="flex items-center gap-2 transition bg-primary text-white dark:text-black px-4 py-2 rounded disabled:opacity-50"
                            >
                                <FiSave /> {saving ? 'Enregistrement...' : 'Enregistrer'}
                            </button>
                            <button
                                onClick={resetForm}
                                disabled={saving}
                                className="flex items-center gap-2 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-2 rounded transition disabled:opacity-50"
                            >
                                <FiX /> Annuler
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Templates List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.length === 0 ? (
                    <div className="col-span-full text-center py-12">
                        <p className="text-gray-500 dark:text-gray-400 mb-4">
                            Aucun modèle disponible
                        </p>
                        {!isCreating && (
                            <button
                                onClick={handleCreate}
                                className="inline-flex items-center gap-2 bg-primary hover:bg-primary-hover dark:text-black text-white px-4 py-2 rounded transition"
                            >
                                <FiPlus /> Créer votre premier modèle
                            </button>
                        )}
                    </div>
                ) : (
                    templates.map((template) => (
                        <div
                            key={template._id}
                            className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-4 shadow hover:shadow-md transition"
                        >
                            <div className="flex justify-between items-start mb-2">
                                <h3 
                                    onClick={() => handlePreview(template)}
                                    className="font-semibold text-gray-900 dark:text-white cursor-pointer hover:text-primary dark:hover:text-primary transition"
                                >
                                    {template.name}
                                </h3>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handlePreview(template)}
                                        className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
                                        title="Aperçu"
                                    >
                                        <FiEye size={16} />
                                    </button>
                                    <button
                                        onClick={() => handleEdit(template)}
                                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                        title="Modifier"
                                    >
                                        <FiEdit2 size={16} />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteClick(template)}
                                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                        title="Supprimer"
                                    >
                                        <FiTrash2 size={16} />
                                    </button>
                                </div>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                {template.title}
                            </p>
                            <div className="flex gap-2 text-xs">
                                <span 
                                    className="px-2 py-1 rounded text-white font-medium"
                                    style={{ backgroundColor: normalizedTypes.find(t => t.name === template.type)?.color || '#3b82f6' }}
                                >
                                    {template.type}
                                </span>
                                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded">
                                    {template.role}
                                </span>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <SuggestionDropdown
                open={suggestOpen}
                items={suggestItems}
                onSelect={applySuggestion}
                anchorRect={suggestAnchor}
                activeIndex={suggestActive}
                onActiveIndexChange={setSuggestActive}
            />
            {canUseDateCommands && (
                <DateDropdown
                    open={dateSuggestOpen}
                    items={dateSuggestItems}
                    onSelect={applyDateSuggestion}
                    anchorRect={dateSuggestAnchor}
                    activeIndex={dateSuggestActive}
                    onActiveIndexChange={setDateSuggestActive}
                />
            )}

            <PreviewTemplateModal
                isOpen={showPreviewModal}
                onClose={() => setShowPreviewModal(false)}
                template={selectedTemplate}
            />

            <DeleteTemplateModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteConfirm}
                templateName={selectedTemplate?.name}
            />
        </div>
    );
};

export default MessageTemplates;
